import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { usePageMotion } from "../hooks/usePageMotion";
import KidsAnimatedBackground from "./KidsAnimatedBackground";
import { ProfileAvatar } from "../pages/ProfilesPage";

const BRAND = "Casasm Kids";

function KidsNavIcon({ name }) {
  const props = {
    className: "kids-nav-icon",
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  switch (name) {
    case "home":
      return (
        <svg {...props}>
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5.5 9.5V21h13V9.5" />
          <path d="M9.5 21v-7h5v7" />
        </svg>
      );
    case "movies":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M7 5v14M17 5v14M3 9h4M3 15h4M17 9h4M17 15h4" />
        </svg>
      );
    case "series":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="12" rx="2" />
          <path d="M8 21h8M12 17v4" />
        </svg>
      );
    case "anime":
      return (
        <svg {...props}>
          <path d="M12 3l1.6 4.8L18.5 9.5l-4.9 1.7L12 16l-1.6-4.8L5.5 9.5l4.9-1.7L12 3z" />
          <path d="M18.5 15.5 19.2 17.7 21.5 18.5l-2.3.8-.7 2.2-.7-2.2-2.3-.8 2.3-.8.7-2.2z" />
        </svg>
      );
    case "tv":
      return (
        <svg {...props}>
          <rect x="3" y="6" width="18" height="13" rx="2" />
          <path d="M8 3 12 6 16 3" />
          <circle cx="12" cy="12.5" r="2.5" fill="currentColor" stroke="none" />
        </svg>
      );
    case "favorites":
      return (
        <svg {...props}>
          <path d="M12 3.6 14.4 9l5.8.5-4.4 3.8 1.4 5.6L12 16.2 6.8 18.9l1.4-5.6L3.8 9.5 9.6 9 12 3.6z" />
        </svg>
      );
    default:
      return null;
  }
}

function KidsNavItem({ to, end, icon, children, onClick }) {
  return (
    <NavLink to={to} end={end} onClick={onClick} className="kids-nav-link">
      <KidsNavIcon name={icon} />
      <span>{children}</span>
    </NavLink>
  );
}

const NAV_ITEMS = [
  { to: "/kids", end: true, icon: "home", label: "Inicio" },
  { to: "/kids/peliculas", icon: "movies", label: "Películas" },
  { to: "/kids/series", icon: "series", label: "Series" },
  { to: "/kids/anime", icon: "anime", label: "Anime" },
  { to: "/kids/tv", icon: "tv", label: "TV" },
  { to: "/kids/favoritos", icon: "favorites", label: "Favoritos" },
];

export default function KidsShell({ children, wide = false }) {
  const { profile, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const mainRef = useRef(null);
  const profileMenuRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [search, setSearch] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get("q") || "";
  });

  usePageMotion(mainRef);

  useEffect(() => {
    document.documentElement.classList.add("kids-mode");
    return () => document.documentElement.classList.remove("kids-mode");
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearch(params.get("q") || "");
  }, [location.search]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setProfileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.body.classList.add("nav-open");
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("nav-open");
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!profileMenuOpen) return undefined;
    function onDoc(e) {
      if (!profileMenuRef.current?.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === "Escape") setProfileMenuOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [profileMenuOpen]);

  function searchBasePath(pathname) {
    if (pathname.startsWith("/kids/peliculas")) return "/kids/peliculas";
    if (pathname.startsWith("/kids/series")) return "/kids/series";
    if (pathname.startsWith("/kids/anime")) return "/kids/anime";
    if (pathname.startsWith("/kids/tv")) return "/kids/tv";
    if (pathname.startsWith("/kids/favoritos")) return "/kids/favoritos";
    return "/kids";
  }

  function applySearch(value) {
    setSearch(value);
    const trimmed = value.trim();
    const base = searchBasePath(location.pathname);
    if (trimmed.length >= 2) {
      navigate(
        { pathname: base, search: `?q=${encodeURIComponent(trimmed)}` },
        { replace: true }
      );
      return;
    }
    navigate({ pathname: base, search: "" }, { replace: true });
  }

  return (
    <div className={`kids-shell ${wide ? "kids-shell-wide" : ""}`}>
      <KidsAnimatedBackground />

      <header
        className={`kids-topbar ${scrolled || menuOpen ? "kids-topbar-solid" : ""}`}
      >
        <div className="kids-topbar-main">
          <Link to="/kids" className="kids-brand" onClick={() => setMenuOpen(false)}>
            <span className="kids-brand-badge" aria-hidden="true">
              CK
            </span>
            <span className="kids-brand-text">
              <span className="kids-brand-casasm">Casasm</span>
              <span className="kids-brand-kids">Kids</span>
            </span>
          </Link>

          <nav className="kids-nav-desktop" aria-label={BRAND}>
            {NAV_ITEMS.map((item) => (
              <KidsNavItem key={item.to} to={item.to} end={item.end} icon={item.icon}>
                {item.label}
              </KidsNavItem>
            ))}
          </nav>

          <div className="kids-topbar-user kids-topbar-user-desktop">
            <label className="kids-topbar-search">
              <span className="sr-only">Buscar</span>
              <input
                type="search"
                placeholder="Películas, series o anime…"
                value={search}
                onChange={(e) => applySearch(e.target.value)}
              />
            </label>
            {profile ? (
              <div className="kids-profile-wrap" ref={profileMenuRef}>
                <button
                  type="button"
                  className="kids-topbar-profile"
                  aria-haspopup="menu"
                  aria-expanded={profileMenuOpen}
                  onClick={() => setProfileMenuOpen((v) => !v)}
                >
                  <ProfileAvatar profile={profile} size={32} className="kids-topbar-avatar" />
                  <span className="kids-topbar-profile-name">{profile.name}</span>
                </button>
                {profileMenuOpen ? (
                  <div className="kids-profile-menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setProfileMenuOpen(false);
                        navigate("/profiles");
                      }}
                    >
                      Cambiar de usuario
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <span className="kids-topbar-email" title={user?.email}>
                {user?.name || user?.email}
              </span>
            )}
          </div>

          <button
            type="button"
            className={`kids-nav-toggle ${menuOpen ? "open" : ""}`}
            aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={menuOpen}
            aria-controls="kids-mobile-nav"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        <div
          id="kids-mobile-nav"
          className={`kids-drawer ${menuOpen ? "open" : ""}`}
        >
          <label className="kids-topbar-search kids-topbar-search-mobile">
            <span className="sr-only">Buscar</span>
            <input
              type="search"
              placeholder="Películas, series o anime…"
              value={search}
              onChange={(e) => applySearch(e.target.value)}
            />
          </label>
          <nav className="kids-nav-mobile" aria-label={`Menú ${BRAND}`}>
            {NAV_ITEMS.map((item) => (
              <KidsNavItem
                key={item.to}
                to={item.to}
                end={item.end}
                icon={item.icon}
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </KidsNavItem>
            ))}
            <button
              type="button"
              className="kids-switch-profile"
              onClick={() => {
                setMenuOpen(false);
                navigate("/profiles");
              }}
            >
              Cambiar de usuario
            </button>
          </nav>
          <div className="kids-topbar-user kids-topbar-user-mobile">
            {profile ? (
              <span className="kids-topbar-profile kids-topbar-profile-static">
                <ProfileAvatar profile={profile} size={32} className="kids-topbar-avatar" />
                <span className="kids-topbar-profile-name">{profile.name}</span>
              </span>
            ) : (
              <span className="kids-topbar-email">{user?.name || user?.email}</span>
            )}
          </div>
        </div>
      </header>

      {menuOpen ? (
        <button
          type="button"
          className="kids-nav-backdrop"
          aria-label="Cerrar menú"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}

      <main ref={mainRef} className="kids-main page-motion-root">
        {children}
      </main>
    </div>
  );
}

export { BRAND as KIDS_BRAND_NAME };
